/**
 * Core authentication domain types
 * These types define the data structures used throughout the authentication system
 */

// Mirrors the backend's `UserRole` Prisma enum (startus-be/prisma/schema.prisma) —
// values must match the strings the API actually sends on `role`.
export const UserRole = {
  USER: 'USER',
  INVESTOR_HUB: 'INVESTOR_HUB',
  ADMIN: 'ADMIN',
} as const;

export type UserRoleType = (typeof UserRole)[keyof typeof UserRole];

// Social media entry for user profile
// id and platform are present on API responses; omit when creating new entries
export interface SocialMedia {
  id?: string;
  platform?: string;
  url: string;
}

// Investor-scoped subrole (e.g. SUPERADMIN/CALIFICADOR/VISUALIZADOR), kept as a
// loose string here — apps that care about specific values narrow/validate it
// themselves (this lib is shared across web/admin/hub and doesn't own that enum).
export interface HubMembership {
  investorId: string;
  role: string;
}

export interface User {
  id: string;
  email: string;
  name: string; // First name
  lastname: string; // Last name
  avatarUrl: string | null;
  isActive: boolean;
  role: UserRoleType;
  socialMedia: SocialMedia[];
  hasAccess: boolean; // true when user has a valid reference code on record
  mustChangePassword?: boolean; // true when user must set a new password before accessing the app
  hubMemberships: HubMembership[]; // investor-team memberships for INVESTOR_HUB users; [] otherwise
}

// Partial user data for updates (excludes immutable fields)
export type UserUpdateData = Partial<
  Pick<User, 'name' | 'lastname' | 'avatarUrl' | 'socialMedia' | 'mustChangePassword'>
>;

export interface LoginCredentials {
  email: string;
  password: string;
  source?: string; // identifies the app origin (e.g. "mvp", "admin")
}

export interface LoginResponse {
  user: User;
  token?: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUser: (data: UserUpdateData) => Promise<void>;
  setAuthFromToken: () => Promise<User | null>;
}
